import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getItems, getVendors, createOrder, quickAddItem, api, getItemsLastOrdered, createItemSuggestion, getOrder, updateOrder, finalizeOrder, getCombinedItems, getCombinedVendors, updateItemVendor, saveDraft, deleteDraft, getDrafts } from "../lib/api";
import { useRestaurant } from "../context/RestaurantContext";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Search, Minus, Plus, Loader2, ShoppingCart, MessageSquare, Truck, Tag, List, PlusCircle, Calendar, Edit3, ChevronLeft, ChevronDown, ChevronUp, SortAsc, CheckCircle, FileEdit } from "lucide-react";

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isCombinedMode, restaurants, currentRestaurant } = useRestaurant();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [orderItems, setOrderItems] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Edit mode state
  const [editOrderId, setEditOrderId] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Draft state (auto-save for new/resumed orders)
  const [draftId, setDraftId] = useState(null);
  const [autoResumed, setAutoResumed] = useState(false);
  const draftStateRef = useRef({});
  const isSubmittedRef = useRef(false);
  
  // Sorting state - persisted in localStorage
  const [mainSort, setMainSort] = useState(() => localStorage.getItem("createOrderMainSort") || "vendor");
  const [subSort, setSubSort] = useState(() => localStorage.getItem("createOrderSubSort") || "category");
  const [visibleCount, setVisibleCount] = useState(25);
  
  // Floating section header state
  const [currentSection, setCurrentSection] = useState("");
  const [showSectionDropdown, setShowSectionDropdown] = useState(false);
  const sectionRefs = useRef({});
  
  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem("createOrderMainSort", mainSort);
  }, [mainSort]);
  
  useEffect(() => {
    localStorage.setItem("createOrderSubSort", subSort);
  }, [subSort]);
  
  // Handle search with native input for better performance
  const handleSearchInput = (e) => {
    const value = e.target.value;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setVisibleCount(25); // Reset visible count on new search
    }, 150);
  };
  
  // Add New Item Dialog
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [selectedRestaurantForItem, setSelectedRestaurantForItem] = useState("");
  const [newItemData, setNewItemData] = useState({
    name: "",
    unit: "pcs",
    category: "",
    pack_size: "",
    default_vendor_id: "",
    quantity_to_order: 1,
    quantity_in_hand: 0,
    comments: "",
  });
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewUnit, setShowNewUnit] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newUnit, setNewUnit] = useState("");
  
  // Last ordered info
  const [lastOrderedInfo, setLastOrderedInfo] = useState({});
  
  // Suggestion dialog state
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [suggestionItem, setSuggestionItem] = useState(null);
  const [suggestionData, setSuggestionData] = useState({ name: "", pack_size: "", description: "" });
  const [isSavingSuggestion, setIsSavingSuggestion] = useState(false);
  
  // Review order dialog state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);

  const restaurantId = localStorage.getItem("selectedRestaurantId");

  // Reload everything whenever the selected restaurant (or combined mode) changes,
  // so drafts/items never leak between restaurants.
  useEffect(() => {
    if (!currentRestaurant?.id) return;
    isSubmittedRef.current = false;
    setDraftId(null);
    setAutoResumed(false);
    setOrderItems({});
    setIsLoading(true);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant?.id]);

  const loadData = async () => {
    try {
      const editId = searchParams.get("edit");
      const draftParam = searchParams.get("draft");
      
      // Use combined endpoints if in combined mode
      const itemsPromise = isCombinedMode ? getCombinedItems(true) : getItems(true);
      const vendorsPromise = isCombinedMode ? getCombinedVendors() : getVendors();
      
      const [itemsRes, vendorsRes, categoriesRes, unitsRes, lastOrderedRes] = await Promise.all([
        itemsPromise,
        vendorsPromise,
        api.get(`/categories?restaurant_id=${restaurantId}`),
        api.get(`/units?restaurant_id=${restaurantId}`),
        getItemsLastOrdered(isCombinedMode || localStorage.getItem("combinedLastOrdered") === "true").catch(() => ({ data: {} })),
      ]);
      
      const sortedItems = itemsRes.data.sort((a, b) => a.name.localeCompare(b.name));
      setItems(sortedItems);
      setVendors(vendorsRes.data);
      setCategories(categoriesRes.data);
      setUnits(unitsRes.data);
      setLastOrderedInfo(lastOrderedRes.data || {});
      
      // Load existing order if in edit mode
      if (editId) {
        try {
          const orderRes = await getOrder(editId);
          setEditOrderId(editId);
          setEditOrder(orderRes.data);
          setIsEditMode(true);

          // Preserve every item that is already part of the order, even if the
          // current catalog no longer surfaces it (e.g. Combined Mode dedup by
          // name collapses same-named items to a single id, or an item was
          // deactivated after the order was placed). Without this, editing an
          // order silently drops those items on submit.
          const existingOrderItems = orderRes.data.items || [];
          const catalogIds = new Set(sortedItems.map((i) => i.id));
          const orphanItems = existingOrderItems
            .filter((oi) => oi.item_id && !catalogIds.has(oi.item_id))
            .map((oi) => ({
              id: oi.item_id,
              name: oi.item_name,
              unit: oi.unit,
              pack_size: oi.pack_size || null,
              category: oi.category || null,
              default_vendor_id: oi.vendor_id,
              is_active: true,
              restaurant_id: restaurantId,
            }));
          const mergedItems = orphanItems.length
            ? [...sortedItems, ...orphanItems].sort((a, b) => a.name.localeCompare(b.name))
            : sortedItems;
          if (orphanItems.length) setItems(mergedItems);

          initializeOrderItemsWithExisting(mergedItems, vendorsRes.data, existingOrderItems);
        } catch (err) {
          toast.error("Failed to load order for editing");
          initializeOrderItems(sortedItems, vendorsRes.data);
        }
      } else if (draftParam) {
        try {
          const draftRes = await getOrder(draftParam);
          setDraftId(draftParam);
          setAutoResumed(true);
          initializeOrderItemsFromDraft(sortedItems, vendorsRes.data, draftRes.data.items || []);
        } catch (err) {
          toast.error("Failed to load draft");
          initializeOrderItems(sortedItems, vendorsRes.data);
        }
      } else {
        // No edit/draft param: auto-resume an unfinished draft for this mode (survives refresh)
        let resumed = false;
        try {
          const draftsRes = await getDrafts();
          const match = (draftsRes.data || []).find((d) =>
            isCombinedMode ? d.is_combined : (!d.is_combined && d.restaurant_id === restaurantId)
          );
          if (match) {
            setDraftId(match.id);
            initializeOrderItemsFromDraft(sortedItems, vendorsRes.data, match.items || []);
            setAutoResumed(true);
            resumed = true;
          }
        } catch (e) { console.warn("Draft auto-resume failed:", e); }
        if (!resumed) initializeOrderItems(sortedItems, vendorsRes.data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize order items with existing order data for edit mode
  const initializeOrderItemsWithExisting = (itemsData, vendorsData, existingItems) => {
    const initialOrderItems = {};
    
    // First, set all items with default values
    itemsData.forEach((item) => {
      const vendor = vendorsData.find(v => v.id === item.default_vendor_id);
      initialOrderItems[item.id] = {
        item_id: item.id,
        item_name: item.name,
        unit: item.unit,
        pack_size: item.pack_size || null,
        category: item.category || null,
        vendor_id: item.default_vendor_id,
        vendor_name: vendor?.name || "Unknown Vendor",
        quantity_in_hand: 0,
        quantity_to_order: 0,
        comments: "",
      };
    });
    
    // Then, apply existing order items
    existingItems.forEach((existingItem) => {
      if (initialOrderItems[existingItem.item_id]) {
        initialOrderItems[existingItem.item_id] = {
          ...initialOrderItems[existingItem.item_id],
          vendor_id: existingItem.vendor_id,
          vendor_name: existingItem.vendor_name,
          quantity_in_hand: existingItem.quantity_in_hand || 0,
          quantity_to_order: existingItem.quantity_to_order || 0,
          comments: existingItem.comments || "",
        };
      } else {
        // Safety net: keep any existing order line even if its item is not in
        // the current catalog fetch, so we never silently drop items on save.
        initialOrderItems[existingItem.item_id] = {
          item_id: existingItem.item_id,
          item_name: existingItem.item_name,
          unit: existingItem.unit,
          pack_size: existingItem.pack_size || null,
          category: existingItem.category || null,
          vendor_id: existingItem.vendor_id,
          vendor_name: existingItem.vendor_name,
          quantity_in_hand: existingItem.quantity_in_hand || 0,
          quantity_to_order: existingItem.quantity_to_order || 0,
          comments: existingItem.comments || "",
        };
      }
    });
    
    setOrderItems(initialOrderItems);
    toast.success(`Editing order #${editOrderId?.substring(0, 8) || ''} - add or modify items`);
  };

  // Initialize order items from a saved draft (not edit mode - submitting creates a new order)
  const initializeOrderItemsFromDraft = (itemsData, vendorsData, draftItems) => {
    const initialOrderItems = {};
    itemsData.forEach((item) => {
      const vendor = vendorsData.find(v => v.id === item.default_vendor_id);
      initialOrderItems[item.id] = {
        item_id: item.id,
        item_name: item.name,
        unit: item.unit,
        pack_size: item.pack_size || null,
        category: item.category || null,
        vendor_id: item.default_vendor_id,
        vendor_name: vendor?.name || "Unknown Vendor",
        quantity_in_hand: 0,
        quantity_to_order: 0,
        comments: "",
      };
    });
    draftItems.forEach((di) => {
      if (initialOrderItems[di.item_id]) {
        initialOrderItems[di.item_id] = {
          ...initialOrderItems[di.item_id],
          vendor_id: di.vendor_id,
          vendor_name: di.vendor_name,
          quantity_in_hand: di.quantity_in_hand || 0,
          quantity_to_order: di.quantity_to_order || 0,
          comments: di.comments || "",
        };
      }
    });
    setOrderItems(initialOrderItems);
    toast.success("Draft loaded - continue where you left off");
  };

  // Collect the order lines that have a quantity to order (used for drafts + submit)
  const getOrderLines = () =>
    Object.values(orderItems).filter((item) => item.quantity_to_order > 0);

  // Discard the current draft and start a fresh empty order
  const startFresh = async () => {
    if (draftId) {
      try {
        await deleteDraft(draftId);
      } catch (e) {
        console.error("Failed to delete draft:", e);
        toast.error("Could not discard the saved draft. Please try again.");
        return;
      }
    }
    setDraftId(null);
    setAutoResumed(false);
    isSubmittedRef.current = false;
    initializeOrderItems(items, vendors);
    toast.success("Started a fresh order");
  };

  // Whether the current session should be auto-saved as a draft.
  // Only new orders and resumed drafts are draftable - not editing already-submitted orders.
  const isDraftable = !isEditMode;

  // Persist the current in-progress order as a draft (debounced auto-save)
  const persistDraft = async () => {
    if (!isDraftable || isSubmittedRef.current) return;
    // Bail if the restaurant changed since this save was scheduled (stale closure)
    if (currentRestaurant?.id !== localStorage.getItem("selectedRestaurantId")) return;
    const lines = getOrderLines();
    if (lines.length === 0) return;
    try {
      const res = await saveDraft({ id: draftId, items: lines, is_combined: isCombinedMode });
      if (res?.data?.id && !draftId) setDraftId(res.data.id);
    } catch (e) {
      console.warn("Draft auto-save failed (will retry):", e);
    }
  };

  // Keep a ref of the latest draft state for beforeunload (refresh/close) saves
  useEffect(() => {
    draftStateRef.current = {
      draftId,
      isDraftable,
      isCombinedMode,
      lines: getOrderLines(),
    };
  });

  // Debounced auto-save whenever the order changes
  useEffect(() => {
    if (!isDraftable) return;
    const t = setTimeout(() => { persistDraft(); }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItems, isDraftable]);

  // Save draft on refresh / tab close (keepalive fetch so it survives unload)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const s = draftStateRef.current;
      if (!s.isDraftable || isSubmittedRef.current || !s.lines || s.lines.length === 0) return;
      try {
        const token = localStorage.getItem("token");
        const rid = localStorage.getItem("selectedRestaurantId");
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/draft?restaurant_id=${rid}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ id: s.draftId, items: s.lines, is_combined: s.isCombinedMode }),
          keepalive: true,
        });
      } catch (e) { console.warn("Draft save on unload failed:", e); }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Initialize order items helper
  const initializeOrderItems = (itemsData, vendorsData) => {
    // Check if we're repeating an order
    const repeatOrder = sessionStorage.getItem("repeatOrder");
    if (repeatOrder && searchParams.get("repeat") === "true") {
      const repeatItems = JSON.parse(repeatOrder);
      const initialOrderItems = {};
      
      itemsData.forEach((item) => {
        const vendor = vendorsData.find(v => v.id === item.default_vendor_id);
        initialOrderItems[item.id] = {
          item_id: item.id,
          item_name: item.name,
          unit: item.unit,
          pack_size: item.pack_size || null,
          category: item.category || null,
          vendor_id: item.default_vendor_id,
          vendor_name: vendor?.name || "Unknown Vendor",
          quantity_in_hand: 0,
          quantity_to_order: 0,
          comments: "",
        };
      });
      
      repeatItems.forEach((repeatItem) => {
        if (initialOrderItems[repeatItem.item_id]) {
          initialOrderItems[repeatItem.item_id] = {
            ...initialOrderItems[repeatItem.item_id],
            vendor_id: repeatItem.vendor_id,
            vendor_name: repeatItem.vendor_name,
            quantity_in_hand: repeatItem.quantity_in_hand || 0,
            quantity_to_order: repeatItem.quantity_to_order || 0,
            comments: repeatItem.comments || "",
          };
        }
      });
      
      setOrderItems(initialOrderItems);
      sessionStorage.removeItem("repeatOrder");
      toast.success("Previous order loaded - modify quantities as needed");
    } else {
      const initialOrderItems = {};
      itemsData.forEach((item) => {
        const vendor = vendorsData.find(v => v.id === item.default_vendor_id);
        initialOrderItems[item.id] = {
          item_id: item.id,
          item_name: item.name,
          unit: item.unit,
          pack_size: item.pack_size || null,
          category: item.category || null,
          vendor_id: item.default_vendor_id,
          vendor_name: vendor?.name || "Unknown Vendor",
          quantity_in_hand: 0,
          quantity_to_order: 0,
          comments: "",
        };
      });
      setOrderItems(initialOrderItems);
    }
  };

  // Simple filtered items
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const search = searchQuery.toLowerCase();
    return items.filter((item) =>
      item.name.toLowerCase().includes(search) || 
      (item.category || '').toLowerCase().includes(search)
    );
  }, [items, searchQuery]);

  // Advanced grouped items with main sort and sub-sort
  const groupedItems = useMemo(() => {
    if (mainSort === "all") {
      // No grouping, just sort by subSort
      let sorted = [...filteredItems];
      if (subSort === "name") {
        sorted.sort((a, b) => a.name.localeCompare(b.name));
      } else if (subSort === "category") {
        sorted.sort((a, b) => {
          const catA = a.category || "Uncategorized";
          const catB = b.category || "Uncategorized";
          if (catA !== catB) return catA.localeCompare(catB);
          return a.name.localeCompare(b.name);
        });
      }
      return { "All Items": sorted };
    }

    if (mainSort === "current") {
      // Only items that are currently part of the order
      const current = filteredItems.filter((i) => (orderItems[i.id]?.quantity_to_order || 0) > 0);
      const sorted = [...current].sort((a, b) => {
        const va = orderItems[a.id]?.vendor_name || "";
        const vb = orderItems[b.id]?.vendor_name || "";
        if (va !== vb) return va.localeCompare(vb);
        return a.name.localeCompare(b.name);
      });
      return { "Current Order": sorted };
    }
    
    // Group by main sort
    const groups = {};
    filteredItems.forEach((item) => {
      let key;
      if (mainSort === "vendor") {
        const vendor = vendors.find(v => v.id === item.default_vendor_id);
        key = vendor?.name || "Unknown Vendor";
      } else if (mainSort === "category") {
        key = item.category || "Uncategorized";
      } else {
        // mainSort === "name" - group by first letter
        key = item.name.charAt(0).toUpperCase();
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    
    // Sort items within each group by subSort
    Object.keys(groups).forEach(key => {
      if (subSort === "name") {
        groups[key].sort((a, b) => a.name.localeCompare(b.name));
      } else if (subSort === "category") {
        groups[key].sort((a, b) => {
          const catA = a.category || "Uncategorized";
          const catB = b.category || "Uncategorized";
          if (catA !== catB) return catA.localeCompare(catB);
          return a.name.localeCompare(b.name);
        });
      }
    });
    
    // Sort group keys
    return Object.keys(groups).sort((a, b) => {
      if (a === "Uncategorized" || a === "Unknown Vendor") return 1;
      if (b === "Uncategorized" || b === "Unknown Vendor") return -1;
      return a.localeCompare(b);
    }).reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, [filteredItems, mainSort, subSort, vendors, orderItems]);

  // Track current section while scrolling
  useEffect(() => {
    if (mainSort === "all") {
      setCurrentSection("");
      return;
    }

    const handleScroll = () => {
      const sections = Object.keys(groupedItems);
      if (sections.length === 0) return;

      const scrollTop = window.scrollY + 220;
      
      let current = sections[0];
      for (const section of sections) {
        const ref = sectionRefs.current[section];
        if (ref && ref.offsetTop <= scrollTop) {
          current = section;
        }
      }
      setCurrentSection(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mainSort, groupedItems]);

  const scrollToSection = (sectionName) => {
    if (sectionName === "end") {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } else {
      const ref = sectionRefs.current[sectionName];
      if (ref) {
        const yOffset = -210;
        const y = ref.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
    setShowSectionDropdown(false);
  };

  const updateOrderItem = (itemId, field, value) => {
    setOrderItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
        ...(field === "vendor_id" && {
          vendor_name: vendors.find((v) => v.id === value)?.name || "Unknown",
        }),
      },
    }));
    
    // Persist vendor change to database
    if (field === "vendor_id" && value) {
      updateItemVendor(itemId, value).catch(() => {
        // Silently fail - vendor will still be used for this order
      });
    }
  };

  const adjustQuantity = (itemId, field, delta) => {
    const currentValue = orderItems[itemId]?.[field] || 0;
    const newValue = Math.max(0, currentValue + delta);
    updateOrderItem(itemId, field, newValue);
  };

  const getItemsToOrderCount = () => {
    return Object.values(orderItems).filter((item) => item.quantity_to_order > 0).length;
  };

  // Open review dialog before submit
  const openReviewDialog = () => {
    const itemsToOrder = Object.values(orderItems).filter(
      (item) => item.quantity_to_order > 0
    );

    if (itemsToOrder.length === 0) {
      toast.error("Please add quantities to order for at least one item");
      return;
    }
    
    // Sort by vendor then by name
    const sorted = [...itemsToOrder].sort((a, b) => {
      if (a.vendor_name !== b.vendor_name) {
        return a.vendor_name.localeCompare(b.vendor_name);
      }
      return a.item_name.localeCompare(b.item_name);
    });
    
    setReviewItems(sorted);
    setShowReviewDialog(true);
  };

  // Update review item
  const updateReviewItem = (index, field, value) => {
    setReviewItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Remove item from review
  const removeReviewItem = (index) => {
    setReviewItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (reviewItems.length === 0) {
      toast.error("No items to order");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode && editOrderId) {
        // Update existing order
        await updateOrder(editOrderId, { items: reviewItems });
        toast.success("Order updated successfully");
        setShowReviewDialog(false);
        // Replace history so back button goes to submitted orders, not edit page
        navigate(`/orders/${editOrderId}`, { replace: true });
      } else {
        // Create new order
        await createOrder({ items: reviewItems });
        isSubmittedRef.current = true;
        // Remove the draft now that it has been submitted
        if (draftId) {
          try { await deleteDraft(draftId); } catch (e) { console.warn("Failed to delete draft after submit:", e); }
        }
        toast.success("Order submitted for review");
        setShowReviewDialog(false);
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit order");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle finalize for edit mode (finalized orders)
  const handleUpdateAndFinalize = async () => {
    if (reviewItems.length === 0) {
      toast.error("No items to order");
      return;
    }

    setIsSubmitting(true);
    try {
      // Update the order
      await updateOrder(editOrderId, { items: reviewItems });
      // Re-finalize if it was already finalized
      if (editOrder?.status === "finalized") {
        await finalizeOrder(editOrderId);
      }
      toast.success("Order updated and finalized");
      setShowReviewDialog(false);
      // Replace history so back button goes to submitted orders, not edit page
      navigate(`/orders/${editOrderId}`, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGroupIcon = (group) => {
    if (mainSort === "vendor") return <Truck className="w-4 h-4" />;
    if (mainSort === "category") return <Tag className="w-4 h-4" />;
    return <List className="w-4 h-4" />;
  };

  // Add New Item handlers
  const openAddItemDialog = () => {
    setNewItemData({
      name: "",
      unit: units[0] || "pcs",
      category: "",
      pack_size: "",
      default_vendor_id: vendors[0]?.id || "",
      quantity_to_order: 1,
      quantity_in_hand: 0,
      comments: "",
    });
    setShowNewCategory(false);
    setShowNewUnit(false);
    setNewCategory("");
    setNewUnit("");
    // Set default restaurant for combined mode
    if (isCombinedMode && restaurants.length > 0) {
      setSelectedRestaurantForItem(restaurants[0].id);
    }
    setIsAddItemOpen(true);
  };

  const handleAddNewItem = async () => {
    if (!newItemData.name.trim()) {
      toast.error("Item name is required");
      return;
    }
    if (!newItemData.default_vendor_id) {
      toast.error("Please select a vendor");
      return;
    }
    if (isCombinedMode && !selectedRestaurantForItem) {
      toast.error("Please select a restaurant");
      return;
    }

    // Determine final unit and category
    const finalUnit = showNewUnit && newUnit.trim() ? newUnit.trim().toLowerCase() : newItemData.unit;
    const finalCategory = showNewCategory && newCategory.trim() ? newCategory.trim() : (newItemData.category === "none" ? null : newItemData.category);

    setIsSavingItem(true);
    try {
      // Create the item in the database using quick-add endpoint (allows managers)
      // In combined mode, use the selected restaurant; otherwise use current restaurant
      const targetRestaurantId = isCombinedMode ? selectedRestaurantForItem : null;
      const response = await quickAddItem({
        name: newItemData.name,
        unit: finalUnit,
        category: finalCategory || null,
        pack_size: newItemData.pack_size || null,
        default_vendor_id: newItemData.default_vendor_id,
      }, targetRestaurantId);

      const newItem = response.data;
      const vendor = vendors.find(v => v.id === newItem.default_vendor_id);

      // Add to items list
      setItems(prev => [...prev, newItem]);

      // Add to order items with the specified quantities
      setOrderItems(prev => ({
        ...prev,
        [newItem.id]: {
          item_id: newItem.id,
          item_name: newItem.name,
          unit: newItem.unit,
          pack_size: newItem.pack_size || null,
          category: newItem.category || null,
          vendor_id: newItem.default_vendor_id,
          vendor_name: vendor?.name || "Unknown",
          quantity_in_hand: newItemData.quantity_in_hand || 0,
          quantity_to_order: newItemData.quantity_to_order || 0,
          comments: newItemData.comments || "",
        },
      }));

      // Update categories and units if new ones were added
      if (showNewCategory && newCategory.trim() && !categories.includes(newCategory.trim())) {
        setCategories(prev => [...prev, newCategory.trim()].sort());
      }
      if (showNewUnit && newUnit.trim() && !units.includes(newUnit.trim().toLowerCase())) {
        setUnits(prev => [...prev, newUnit.trim().toLowerCase()].sort());
      }

      toast.success(`"${newItem.name}" added to order`);
      setIsAddItemOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    } finally {
      setIsSavingItem(false);
    }
  };

  const handleSubmitSuggestion = async () => {
    if (!suggestionItem) return;
    
    // Check if anything changed
    const changes = {};
    if (suggestionData.name !== suggestionItem.name) changes.name = suggestionData.name;
    if (suggestionData.pack_size !== (suggestionItem.pack_size || "")) changes.pack_size = suggestionData.pack_size;
    if (suggestionData.description !== (suggestionItem.description || "")) changes.description = suggestionData.description;
    
    if (Object.keys(changes).length === 0) {
      toast.error("No changes to suggest");
      return;
    }
    
    setIsSavingSuggestion(true);
    try {
      await createItemSuggestion({
        item_id: suggestionItem.id,
        source: "manual",
        suggested_changes: changes
      });
      toast.success("Suggestion submitted for admin review");
      setIsSuggestOpen(false);
      setSuggestionItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit suggestion");
    } finally {
      setIsSavingSuggestion(false);
    }
  };

  const pageTitle = isEditMode 
    ? `Edit Order #${editOrder?.order_name || editOrderId?.substring(0, 8) || ''}` 
    : "Create Order";

  return (
    <AppLayout title={pageTitle} showBack>
      <div className="p-4 md:p-6 pb-32 max-w-2xl mx-auto">
        {/* Resumed Draft Banner */}
        {autoResumed && !isEditMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center justify-between gap-2" data-testid="resumed-draft-banner">
            <div className="flex items-center gap-2 min-w-0">
              <FileEdit className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800 truncate">Resumed your unfinished order</p>
            </div>
            <Button size="sm" variant="outline" onClick={startFresh} className="shrink-0 h-8 border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="start-fresh-button">
              Start fresh
            </Button>
          </div>
        )}

        {/* Edit Mode Banner */}
        {isEditMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800 text-sm">
                Editing existing order
              </p>
              <p className="text-xs text-blue-600">
                Add items or modify quantities, then submit to update
              </p>
            </div>
          </div>
        )}

        {/* Initial Sort selector - visible before scrolling, scrolls with content */}
        <div className="bg-bone pt-2 pb-3 -mx-4 px-4 space-y-3 border-b border-stone-100">
          {/* Combined Mode Banner */}
          {isCombinedMode && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 flex items-center gap-2">
              <Truck className="w-4 h-4 text-purple-600" />
              <p className="text-xs text-purple-700 font-medium">
                Combined Order Mode - Items from all locations
              </p>
            </div>
          )}

          {/* Sort Controls - search is up in the floating dark strip */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <span className="text-xs text-stone-500 mb-1 block">Sort by:</span>
              <Select value={mainSort} onValueChange={(v) => { setMainSort(v); localStorage.setItem("orderMainSort", v); }}>
                <SelectTrigger className="h-10" data-testid="main-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4" />
                      Current Order
                    </div>
                  </SelectItem>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <List className="w-4 h-4" />
                      All Items
                    </div>
                  </SelectItem>
                  <SelectItem value="vendor">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Vendor
                    </div>
                  </SelectItem>
                  <SelectItem value="category">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Category
                    </div>
                  </SelectItem>
                  <SelectItem value="name">
                    <div className="flex items-center gap-2">
                      <SortAsc className="w-4 h-4" />
                      Name (A-Z)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mainSort !== "all" && mainSort !== "current" && (
              <div className="flex-1">
                <span className="text-xs text-stone-500 mb-1 block">Then by:</span>
                <Select value={subSort} onValueChange={(v) => { setSubSort(v); localStorage.setItem("orderSubSort", v); }}>
                  <SelectTrigger className="h-10" data-testid="sub-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">
                      <div className="flex items-center gap-2">
                        <SortAsc className="w-4 h-4" />
                        Name (A-Z)
                      </div>
                    </SelectItem>
                    <SelectItem value="category">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Category
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Floating Dark Strip - search + add for ALL sort modes. Also shows section jump for grouped modes. */}
        <div className="fixed top-0 left-0 right-0 z-[50]">
          <div className="bg-stone-900 text-white px-4 shadow-xl h-14 flex items-center">
            <div className="max-w-2xl mx-auto w-full">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors shrink-0"
                  data-testid="back-button-floating"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Section jump (grouped modes only) */}
                {mainSort !== "all" && mainSort !== "current" && currentSection && (
                  <button
                    className="flex items-center gap-1.5 cursor-pointer min-w-0 shrink-0 hover:bg-white/10 rounded-lg px-2 py-1 transition-colors max-w-[42%]"
                    onClick={() => setShowSectionDropdown(!showSectionDropdown)}
                    data-testid="section-jump-button"
                  >
                    {mainSort === "vendor" ? <Truck className="w-4 h-4 shrink-0" /> : mainSort === "category" ? <Tag className="w-4 h-4 shrink-0" /> : <SortAsc className="w-4 h-4 shrink-0" />}
                    <span className="font-medium text-sm truncate">{currentSection}</span>
                    {showSectionDropdown ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
                  </button>
                )}

                {/* Search */}
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    ref={searchInputRef}
                    type="search"
                    placeholder="Search..."
                    defaultValue=""
                    onInput={handleSearchInput}
                    className="flex h-8 w-full rounded-md border border-stone-600 bg-stone-800 px-2 py-1 pl-8 text-sm text-white placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-kale-500"
                    data-testid="search-order-items"
                  />
                </div>

                <Button
                  type="button"
                  onClick={openAddItemDialog}
                  className="h-8 w-8 p-0 bg-kale-600 hover:bg-kale-700 shrink-0"
                  data-testid="quick-add-item-btn"
                  title="Add new item"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {showSectionDropdown && (
            <div className="px-4">
              <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-b-lg shadow-xl border border-stone-200 border-t-0 max-h-[300px] overflow-y-auto">
                  {Object.keys(groupedItems).map((section) => (
                    <button
                      key={section}
                      onClick={() => scrollToSection(section)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-stone-50 transition-colors flex items-center justify-between ${
                        section === currentSection ? "bg-kale-50 text-kale-700 font-medium" : "text-stone-700"
                      }`}
                    >
                      <span className="text-sm">{section}</span>
                      <Badge variant="secondary" className="text-xs font-mono">
                        {groupedItems[section].length}
                      </Badge>
                    </button>
                  ))}
                  <button
                    onClick={() => scrollToSection("end")}
                    className="w-full text-left px-4 py-2.5 hover:bg-stone-50 transition-colors text-stone-500 border-t text-sm"
                  >
                    ↓ Jump to End
                  </button>
                  <button
                    onClick={() => { setShowSectionDropdown(false); openReviewDialog(); }}
                    disabled={getItemsToOrderCount() === 0}
                    className="w-full text-left px-4 py-2.5 bg-clay-50 hover:bg-clay-100 transition-colors text-clay-700 font-semibold border-t text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="dropdown-submit-order"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    {isEditMode ? "Update Order" : "Submit Order"} ({getItemsToOrderCount()})
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : Object.keys(groupedItems).length === 0 || Object.values(groupedItems).every(g => g.length === 0) ? (
          <div className="text-center py-12">
            <p className="text-stone-500">
              {searchQuery ? "No items match your search" : mainSort === "current" ? "No items added to this order yet. Switch to another view to add items." : "No active items available"}
            </p>
          </div>
        ) : (
          <div className={`space-y-6 ${mainSort !== "all" ? "pt-12" : ""}`}>
            {Object.entries(groupedItems).map(([groupName, groupItemsList]) => {
              // Limit items when viewing "All Items" (not grouped)
              const visibleItems = mainSort === "all" 
                ? groupItemsList.slice(0, visibleCount) 
                : groupItemsList;
              const hasMore = mainSort === "all" && groupItemsList.length > visibleCount;
              
              return (
              <div key={groupName} className="space-y-3" ref={(el) => { sectionRefs.current[groupName] = el; }}>
                {/* Group Header */}
                {mainSort !== "all" && (
                  <div className="flex items-center gap-2 px-1 py-2">
                    <div className="flex items-center gap-2 text-kale-700">
                      {mainSort === "vendor" ? <Truck className="w-4 h-4" /> : mainSort === "category" ? <Tag className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
                      <h3 className="font-semibold">{groupName}</h3>
                    </div>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {groupItemsList.length}
                    </Badge>
                  </div>
                )}
                
                {/* Items */}
                {visibleItems.map((item) => {
                  const orderItem = orderItems[item.id];
                  const lastOrdered = lastOrderedInfo[item.id];
                  return (
                    <Card key={item.id} className="card-hover" data-testid={`order-item-${item.id}`}>
                      <CardContent className="p-4 space-y-4">
                        {/* Item Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-stone-900">{item.name}</h3>
                              {item.category && mainSort !== "category" && (
                                <Badge variant="secondary" className="bg-kale-50 text-kale-700 text-xs">
                                  {item.category}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="outline" className="bg-white/50 backdrop-blur-sm border-stone-200 text-stone-600 text-xs font-normal">
                                {item.unit}
                              </Badge>
                              {item.pack_size && (
                                <Badge variant="outline" className="bg-white/50 backdrop-blur-sm border-stone-200 text-stone-600 text-xs font-normal">
                                  {item.pack_size}
                                </Badge>
                              )}
                            </div>
                            {lastOrdered && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-kale-600">
                                <Calendar className="w-3 h-3" />
                                <span>Last: {lastOrdered.last_ordered_quantity} on {lastOrdered.last_ordered_date}</span>
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-stone-400 hover:text-kale-600"
                            onClick={() => {
                              setSuggestionItem(item);
                              setSuggestionData({ name: item.name, pack_size: item.pack_size || "", description: item.description || "" });
                              setIsSuggestOpen(true);
                            }}
                            data-testid={`suggest-edit-${item.id}`}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Vendor Selector */}
                        <Select value={orderItem?.vendor_id} onValueChange={(v) => updateOrderItem(item.id, "vendor_id", v)}>
                          <SelectTrigger className="h-11" data-testid={`vendor-select-${item.id}`}>
                            <SelectValue placeholder="Select vendor" />
                          </SelectTrigger>
                          <SelectContent>
                            {vendors.map((vendor) => (
                              <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Quantity Inputs */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-stone-500 mb-1 block">In Hand</label>
                            <div className="flex items-center gap-1">
                              <Button type="button" variant="outline" size="icon" className="h-11 w-11"
                                onClick={() => adjustQuantity(item.id, "quantity_in_hand", -1)}>
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input type="number" min="0" placeholder="0" value={orderItem?.quantity_in_hand || ""}
                                onChange={(e) => updateOrderItem(item.id, "quantity_in_hand", e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0))}
                                onFocus={(e) => e.target.select()}
                                data-testid={`inhand-${item.id}`}
                                className="h-11 text-center font-mono" />
                              <Button type="button" variant="outline" size="icon" className="h-11 w-11"
                                onClick={() => adjustQuantity(item.id, "quantity_in_hand", 1)}>
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-stone-500 mb-1 block">To Order</label>
                            <div className="flex items-center gap-1">
                              <Button type="button" variant="outline" size="icon" className="h-11 w-11"
                                onClick={() => adjustQuantity(item.id, "quantity_to_order", -1)}>
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input type="number" min="0" placeholder="0" value={orderItem?.quantity_to_order || ""}
                                onChange={(e) => updateOrderItem(item.id, "quantity_to_order", e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0))}
                                onFocus={(e) => e.target.select()}
                                data-testid={`toorder-${item.id}`}
                                className="h-11 text-center font-mono" />
                              <Button type="button" variant="outline" size="icon" className="h-11 w-11"
                                onClick={() => adjustQuantity(item.id, "quantity_to_order", 1)}>
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Comments */}
                        <div className="relative">
                          <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-stone-400" />
                          <Textarea
                            placeholder="Comments (optional)"
                            value={orderItem?.comments || ""}
                            onChange={(e) => updateOrderItem(item.id, "comments", e.target.value)}
                            className="pl-10 min-h-[44px] resize-none text-base"
                            rows={1}
                            data-testid={`comments-${item.id}`}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {/* Load More Button */}
                {hasMore && (
                  <Button
                    variant="outline"
                    className="w-full py-6 text-kale-700 border-kale-300"
                    onClick={() => setVisibleCount(prev => prev + 25)}
                    data-testid="load-more-items"
                  >
                    Load More ({groupItemsList.length - visibleCount} remaining)
                  </Button>
                )}
              </div>
            )})}

            {/* Add New Item Card */}
            <Card 
              className="border-2 border-dashed border-kale-300 bg-kale-50/50 hover:bg-kale-50 cursor-pointer transition-colors"
              onClick={openAddItemDialog}
              data-testid="add-new-item-card"
            >
              <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                <PlusCircle className="w-10 h-10 text-kale-600 mb-2" />
                <h3 className="font-semibold text-kale-800">Add New Item</h3>
                <p className="text-sm text-kale-600">Create a new item and add it to this order</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Sticky Submit Button */}
      <div className="sticky-bottom">
        <Button
          onClick={openReviewDialog}
          disabled={isSubmitting || getItemsToOrderCount() === 0}
          className="w-full btn-accent"
          data-testid="review-order-button"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {isEditMode ? "Updating..." : "Submitting..."}
            </>
          ) : (
            <>
              <ShoppingCart className="w-4 h-4 mr-2" />
              {isEditMode ? `Update Order (${getItemsToOrderCount()} items)` : `Review Order (${getItemsToOrderCount()} items)`}
            </>
          )}
        </Button>
      </div>

      {/* Review Order Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-kale-600" />
              {isEditMode ? "Review Changes" : "Review Order"}
            </DialogTitle>
            <DialogDescription>
              {isEditMode 
                ? "Review updated items before saving changes."
                : "Review items before submitting. You can edit quantities or remove items."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4 max-h-[50vh] overflow-y-auto">
            {reviewItems.map((item, index) => (
              <div key={item.item_id} className="p-3 bg-stone-50 rounded-lg">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.item_name}</p>
                    {/* Vendor Selector */}
                    <Select 
                      value={item.vendor_id} 
                      onValueChange={(value) => {
                        const vendor = vendors.find(v => v.id === value);
                        updateReviewItem(index, 'vendor_id', value);
                        updateReviewItem(index, 'vendor_name', vendor?.name || 'Unknown');
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs mt-1 w-full">
                        <Truck className="w-3 h-3 mr-1 text-stone-400" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((v) => (
                          <SelectItem key={v.id} value={v.id} className="text-xs">
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeReviewItem(index)}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500 w-16">Qty:</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => updateReviewItem(index, 'quantity_to_order', Math.max(1, item.quantity_to_order - 1))}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity_to_order === "" ? "" : item.quantity_to_order}
                    onChange={(e) => updateReviewItem(index, 'quantity_to_order', e.target.value === "" ? "" : Math.max(1, parseFloat(e.target.value) || 1))}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => { if (e.target.value === "") updateReviewItem(index, 'quantity_to_order', 1); }}
                    className="h-8 w-16 text-center font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => updateReviewItem(index, 'quantity_to_order', item.quantity_to_order + 1)}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                  <span className="text-xs text-stone-500">{item.unit}</span>
                </div>
              </div>
            ))}
            
            {reviewItems.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                No items to order
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col gap-2">
            <div className="flex gap-2 w-full">
              <Button
                variant="outline"
                onPointerDown={() => { const a = document.activeElement; if (a && typeof a.blur === "function") a.blur(); }}
                onClick={() => setShowReviewDialog(false)}
                className="flex-1"
                style={{ touchAction: "manipulation" }}
              >
                Back to Edit
              </Button>
              <Button 
                onPointerDown={() => { const a = document.activeElement; if (a && typeof a.blur === "function") a.blur(); }}
                onClick={handleSubmit} 
                disabled={isSubmitting || reviewItems.length === 0}
                className="flex-1 btn-accent"
                style={{ touchAction: "manipulation" }}
                data-testid="confirm-submit-button"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ShoppingCart className="w-4 h-4 mr-2" />
                )}
                {isEditMode ? "Update Order" : `Submit Order (${reviewItems.length})`}
              </Button>
            </div>
            {isEditMode && editOrder?.status === "finalized" && (
              <Button 
                onPointerDown={() => { const a = document.activeElement; if (a && typeof a.blur === "function") a.blur(); }}
                onClick={handleUpdateAndFinalize} 
                disabled={isSubmitting || reviewItems.length === 0}
                className="w-full btn-primary"
                style={{ touchAction: "manipulation" }}
                data-testid="update-finalize-button"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Update & Re-Finalize
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Item Dialog */}
      <Dialog open={isAddItemOpen} onOpenChange={setIsAddItemOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-item-dialog">
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
            <DialogDescription>
              Create a new item and add it to your order
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Restaurant Selector - Only in Combined Mode */}
            {isCombinedMode && (
              <div className="space-y-2">
                <Label htmlFor="new-item-restaurant">Restaurant *</Label>
                <Select 
                  value={selectedRestaurantForItem} 
                  onValueChange={setSelectedRestaurantForItem}
                >
                  <SelectTrigger className="h-12" data-testid="new-item-restaurant">
                    <SelectValue placeholder="Select restaurant" />
                  </SelectTrigger>
                  <SelectContent>
                    {restaurants.map((restaurant) => (
                      <SelectItem key={restaurant.id} value={restaurant.id}>
                        {restaurant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Item Name */}
            <div className="space-y-2">
              <Label htmlFor="new-item-name">Item Name *</Label>
              <Input
                id="new-item-name"
                value={newItemData.name}
                onChange={(e) => setNewItemData({ ...newItemData, name: e.target.value })}
                placeholder="e.g., Fresh Basil"
                className="input-mobile"
                data-testid="new-item-name"
              />
            </div>

            {/* Unit */}
            <div className="space-y-2">
              <Label>Unit *</Label>
              {!showNewUnit ? (
                <div className="flex gap-2">
                  <Select 
                    value={newItemData.unit} 
                    onValueChange={(v) => setNewItemData({ ...newItemData, unit: v })}
                  >
                    <SelectTrigger className="h-12 flex-1" data-testid="new-item-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowNewUnit(true)}
                    className="h-12 px-3"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={newUnit}
                    onChange={(e) => setNewUnit(e.target.value)}
                    placeholder="Enter new unit"
                    className="input-mobile flex-1"
                    data-testid="new-unit-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setShowNewUnit(false); setNewUnit(""); }}
                    className="h-12 px-3"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category</Label>
              {!showNewCategory ? (
                <div className="flex gap-2">
                  <Select 
                    value={newItemData.category || "none"} 
                    onValueChange={(v) => setNewItemData({ ...newItemData, category: v })}
                  >
                    <SelectTrigger className="h-12 flex-1" data-testid="new-item-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowNewCategory(true)}
                    className="h-12 px-3"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter new category"
                    className="input-mobile flex-1"
                    data-testid="new-category-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setShowNewCategory(false); setNewCategory(""); }}
                    className="h-12 px-3"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* Vendor */}
            <div className="space-y-2">
              <Label>Default Vendor *</Label>
              <Select 
                value={newItemData.default_vendor_id} 
                onValueChange={(v) => setNewItemData({ ...newItemData, default_vendor_id: v })}
              >
                <SelectTrigger className="h-12" data-testid="new-item-vendor">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Pack Size */}
            <div className="space-y-2">
              <Label htmlFor="new-item-pack">Pack Size</Label>
              <Input
                id="new-item-pack"
                value={newItemData.pack_size}
                onChange={(e) => setNewItemData({ ...newItemData, pack_size: e.target.value })}
                placeholder="e.g., 100g bunch"
                className="input-mobile"
              />
            </div>

            {/* Order Quantities */}
            <div className="bg-kale-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-kale-800 text-sm">Add to Order</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">In Hand</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={newItemData.quantity_in_hand || ""}
                    onChange={(e) => setNewItemData({ ...newItemData, quantity_in_hand: e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0) })}
                    onFocus={(e) => e.target.select()}
                    className="h-11 text-center font-mono"
                    data-testid="new-item-inhand"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To Order</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={newItemData.quantity_to_order || ""}
                    onChange={(e) => setNewItemData({ ...newItemData, quantity_to_order: e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0) })}
                    onFocus={(e) => e.target.select()}
                    className="h-11 text-center font-mono"
                    data-testid="new-item-toorder"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Comments</Label>
                <Textarea
                  value={newItemData.comments}
                  onChange={(e) => setNewItemData({ ...newItemData, comments: e.target.value })}
                  placeholder="Optional comments"
                  className="min-h-[44px] resize-none text-sm"
                  rows={1}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsAddItemOpen(false)} className="flex-1 h-11">
              Cancel
            </Button>
            <Button 
              onClick={handleAddNewItem} 
              className="flex-1 btn-primary" 
              disabled={isSavingItem}
              data-testid="save-new-item"
            >
              {isSavingItem ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suggest Edit Dialog */}
      <Dialog open={isSuggestOpen} onOpenChange={setIsSuggestOpen}>
        <DialogContent className="sm:max-w-md" data-testid="suggest-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-kale-600" />
              Suggest Edit
            </DialogTitle>
            <DialogDescription>
              Suggest changes to this item. An admin will review and approve.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="suggest-name">Item Name</Label>
              <Input
                id="suggest-name"
                value={suggestionData.name}
                onChange={(e) => setSuggestionData({ ...suggestionData, name: e.target.value })}
                className="input-mobile"
                data-testid="suggest-name-input"
              />
              {suggestionItem && suggestionData.name !== suggestionItem.name && (
                <p className="text-xs text-amber-600">
                  Was: {suggestionItem.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="suggest-pack">Pack Size</Label>
              <Input
                id="suggest-pack"
                value={suggestionData.pack_size}
                onChange={(e) => setSuggestionData({ ...suggestionData, pack_size: e.target.value })}
                placeholder="e.g., 5kg box"
                className="input-mobile"
                data-testid="suggest-pack-input"
              />
              {suggestionItem && suggestionData.pack_size !== (suggestionItem.pack_size || "") && (
                <p className="text-xs text-amber-600">
                  Was: {suggestionItem.pack_size || "(empty)"}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="suggest-desc">Description</Label>
              <Textarea
                id="suggest-desc"
                value={suggestionData.description}
                onChange={(e) => setSuggestionData({ ...suggestionData, description: e.target.value })}
                placeholder="Add a description..."
                className="min-h-[60px] resize-none"
                data-testid="suggest-desc-input"
              />
              {suggestionItem && suggestionData.description !== (suggestionItem.description || "") && (
                <p className="text-xs text-amber-600">
                  Was: {suggestionItem.description || "(empty)"}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsSuggestOpen(false)} className="flex-1 h-11">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitSuggestion} 
              className="flex-1 btn-primary" 
              disabled={isSavingSuggestion}
              data-testid="submit-suggestion-btn"
            >
              {isSavingSuggestion ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Suggestion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
